import Button from "@/components/Button";
import { TreatmentNameToInitialQuestionnaireIDMapping } from "@/constants/Mappings";
import Image from "next/image";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import React from "react";

const SafetySection = ({ treatmentName }: { treatmentName: string }) => {
    const router = useRouter();
    const teamMembers = [
        {
            name: "llene S. Ruhoy",
            title: "MD, PhD, Neurologist",
            image: "/images/marketing/safety/Ilene.png",
        },
        {
            name: "Sarah Almilli",
            title: "PharmD, APh., IA, Clinical Pharmacist",
            image: "/images/marketing/safety/Sarah.png",
        },
        {
            name: "Stuart Malcolm",
            title: "MD, Internist",
            image: "/images/marketing/safety/Stuart.png",
        },
        {
            name: "Jennifer Curtin",
            title: "MD, Internist",
            image: "/images/marketing/safety/Jen.png",
        },
    ];

    const safetyPoints = [
        "1. All treatments, including pyridostigmine, are carefully reviewed by RTHM clinicians and pharmacists.",
        "2. We use dosing protocols designed to minimize side effects and ensure safe use.",
        "3. Comprehensive treatment guides are vetted by our expert clinical team.",
        "As you navigate your health journey, we're here to provide safe and effective care.",
    ];

    const initialQuestionnaireId =
        TreatmentNameToInitialQuestionnaireIDMapping[treatmentName];

    return (
        <div className="w-full bg-gray-50 dark:bg-gray-700 rounded-2xl p-6 md:p-12 z-20">
            <div className="flex flex-col gap-4">
                {/* Title - Mobile Only */}
                <h2 className="text-[#333333] dark:text-white text-3xl md:text-5xl font-bold font-satoshi uppercase leading-tight lg:hidden">
                    Safety Is Our Top Priority
                </h2>

                {/* Mobile Team Grid */}
                <div className="w-full lg:hidden">
                    <div className="grid grid-cols-2 gap-4">
                        {teamMembers.map((member, index) => (
                            <div
                                key={index}
                                className="rounded-xl overflow-hidden"
                            >
                                <div className="aspect-w-1 aspect-h-1">
                                    <Image
                                        src={member.image}
                                        alt={member.name}
                                        width={0}
                                        height={0}
                                        sizes="100vw"
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                        }}
                                    />
                                </div>
                                <div className="p-4 text-center">
                                    <h3 className="text-[#1a5b77] dark:text-blue-300 text-xl font-medium font-satoshi leading-relaxed">
                                        {member.name}
                                    </h3>
                                    <p className="text-[#333333] dark:text-gray-200 text-sm font-light font-space leading-snug">
                                        {member.title}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Desktop layout */}
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-stretch gap-12 min-h-full">
                    {/* Left Content - Title and Safety Points */}
                    <div className="lg:w-2/5 flex flex-col justify-center">
                        {/* Title - Desktop Only */}
                        <h2 className="hidden lg:block text-[#333333] dark:text-white text-3xl md:text-5xl font-semibold font-satoshi uppercase leading-tight mb-12">
                            Safety Is Our Top Priority
                        </h2>

                        {/* Safety Points */}
                        <div className="space-y-4 mb-12">
                            {safetyPoints.map((point, index) => (
                                <p
                                    key={index}
                                    className="text-[#333333] dark:text-gray-200 text-lg font-light font-space leading-relaxed"
                                >
                                    {point}
                                </p>
                            ))}
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col gap-4">
                            {initialQuestionnaireId && (
                                <Button
                                    text="Start My Safety Assessment Today"
                                    id={
                                        "start-safety-assessment-safety-" +
                                        treatmentName
                                    }
                                    type="filled"
                                    onClick={() => {
                                        posthog?.capture(
                                            "start_safety_assessment",
                                            {
                                                part: "safety",
                                                treatmentName: treatmentName,
                                            },
                                        );
                                        router.push(
                                            `/orders/${initialQuestionnaireId}`,
                                        );
                                    }}
                                    className="uppercase"
                                    height="h-16"
                                />
                            )}
                        </div>
                    </div>

                    {/* Desktop Team Grid */}
                    <div className="hidden lg:block lg:w-6/12">
                        <div className="grid grid-cols-2 gap-4">
                            {teamMembers.map((member, index) => (
                                <div
                                    key={index}
                                    className="rounded-xl overflow-hidden"
                                >
                                    <Image
                                        src={member.image}
                                        alt={member.name}
                                        width={0}
                                        height={0}
                                        sizes="100vw"
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                        }}
                                    />
                                    <div className="p-4 text-center">
                                        <h3 className="text-[#1a5b77] dark:text-blue-300 text-xl font-medium font-satoshi leading-relaxed">
                                            {member.name}
                                        </h3>
                                        <p className="text-[#333333] dark:text-gray-200 text-sm font-light font-space leading-snug">
                                            {member.title}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SafetySection;
